import { IonButton, IonIcon, IonText as Text } from '@ionic/react';
import { arrowBack, mailOutline } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { t } from 'ttag';

import { UserSession } from '@custom-types/UserSession';
import useToast from '@hooks/useToast';

import { ErrorCode, errorMessages, sendEmailLink as sendEmailLinkFn } from './service';

interface Props {
  user: Partial<UserSession>;
  goBack: () => void;
}

const Box = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
`;
const Heading = styled.div`
  margin: 24px 0;
  text-align: center;
  font-weight: 500;
`;
const EmailHolder = styled.span`
  font-weight: 800;
  font-size: 18px;
`;
const Button = styled(IonButton)`
  width: 100%;
`;
const Container = styled(Box)`
  flex: 1;
  width: 100%;
`;
const IonText = styled(Text)`
  text-align: center;
`;
const Icon = styled(IonIcon)`
  margin-top: 40px;
  color: #ff9e1b;
  width: 138px;
  height: 138px;
`;

const DURATION = 4000;

export const useSendEmailLink = () => {
  const showToast = useToast();
  const [isResending, setIsResending] = useState(false);
  const [isEmailLinkSent, setIsEmailLinkSent] = useState(false);
  const timerId = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerId.current) {
        window.clearTimeout(timerId.current);
      }
    },
    [timerId]
  );

  const sendEmailLink = async (email?: string) => {
    if (!email) {
      return;
    }

    setIsResending(true);
    try {
      await sendEmailLinkFn(email);
      showToast(t`Email Sent!`, DURATION);
      setIsEmailLinkSent(true);
    } catch (error) {
      showToast(errorMessages((error as { type: ErrorCode }).type), DURATION);
      setIsEmailLinkSent(false);
    } finally {
      timerId.current = window.setTimeout(() => setIsResending(false), DURATION);
    }
  };

  return { isResending, isEmailLinkSent, sendEmailLink };
};

const EmailLinkSuccess = ({ user, goBack }: Props) => {
  const { isResending, sendEmailLink } = useSendEmailLink();

  return (
    <Container>
      <Box>
        <Icon icon={mailOutline} />
        <IonText>
          <h1>{t`Almost Done!`}</h1>
        </IonText>
        <Heading>
          <p className="ion-no-margin">{t`We’ve sent an email to you at`}</p>
          <EmailHolder>{user.email}</EmailHolder>
          <IonText>
            <p>{t`Please click on the link in your email to log in.`}</p>
          </IonText>
        </Heading>
      </Box>
      <div>
        <IonText>
          <p className="ion-no-margin">{t`Don’t see the email? Check your spam folder or`}</p>
        </IonText>
        <Button
          className="ion-no-margin"
          type="button"
          role="button"
          fill="clear"
          // skipcq: JS-0417, JS-0336
          onClick={() => sendEmailLink(user.email)}
          disabled={isResending}
        >
          {t`Click here to send new email`}
        </Button>

        <Button
          type="button"
          role="button"
          expand="block"
          fill="outline"
          onClick={goBack}
          className="ion-padding-vertical"
        >
          <IonIcon slot="start" icon={arrowBack} />
          {t`Back`}
        </Button>
      </div>
    </Container>
  );
};

export default EmailLinkSuccess;
